import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Figure, Flow } from '../components/Diagram';
import { UiPath } from '../components/UiPath';

const STEPS: {
  title: string;
  body: ReactNode;
  where?: [string, ...string[]];
  whereNote?: string;
  figure?: ReactNode;
}[] = [
  {
    title: 'Before you start',
    body: 'The Inbox appears in the sidebar under Workspace once your workspace has the Work Inbox feature. It is switched on per workspace and is meant for workspaces that use the CRM. Each person then connects their own Gmail mailbox on their account page with Connect Gmail. Only Gmail feeds the inbox today; an Outlook mailbox does not. If your mailbox was connected before the inbox was switched on, the last 30 days of mail are brought in, and nothing older.',
    where: ['My Account Settings', 'Gmail', 'Connect Gmail'],
    whereNote: 'My Account Settings is in the menu on your name at the bottom of the sidebar.',
  },
  {
    title: 'The five tabs',
    body: 'Primary holds the mail that needs a person: clients, prospects, new contacts and your team. Needs reply lists threads where someone asked you something and you have not answered; a plain thank-you does not count, and such a thread is marked No reply needed. Other holds the rest of the sorted mail: bills, tools, newsletters and promotions. Snoozed holds threads you put aside until later. Done holds what you have finished. A thread in Done comes back to the inbox when a new message arrives on it.',
    where: ['Inbox'],
  },
  {
    title: 'Categories and where they land',
    body: 'Every thread gets one category: Client, Prospect, New contact, Team, Calendar, Payments, Receivables, Bills, HR, Legal, Tools, Security, Newsletters, Promotions, Personal or Unsorted. By default Client, Prospect, New contact, Team, Calendar, Payments, Receivables and Unsorted land in Primary, and the others land in Other. Unsorted is mail nothing could sort. It always stays in Primary, so mail is never hidden because it could not be filed. Each thread shows a one-line reason for its category, and the category chips above the list narrow a tab to one category.',
  },
  {
    title: 'Gmail stays the source of truth',
    body: 'Marking a thread Done archives it in Gmail. Read and unread, and starred, are the same in both places. Each of these changes is made in Gmail first, and Revvly shows it only once Gmail has accepted it. A change you make in Gmail shows in Revvly too. If Gmail refuses a change, the thread stays as it was and Revvly says so.',
  },
  {
    title: 'Snooze a thread',
    body: 'Snooze, beside Done, offers Later today (three hours from now), Tomorrow (09:00), Next week (Monday 09:00) and Pick a date and time. The times are on your own clock. A snoozed thread comes back at that time, or sooner when a new message arrives on it. Gmail has no snooze of its own, so in Gmail a snoozed thread looks archived until it comes back.',
  },
  {
    title: 'Teach it where mail goes',
    body: 'The inbox learns from what you do, and only for you; a colleague’s choices never change your inbox. Move a thread to another category with Move to, and that thread keeps your choice for good. Two moves of the same address to the same category file that address there from then on, and three for a domain. Archiving five messages in a row from a sender without opening them moves that sender to Other; Move back to Primary undoes that and keeps the sender in Primary. Free email domains such as Gmail addresses are never learned domain-wide, because many unrelated people share them. What was learned never changes the category of mail from a client or a colleague. When a thread is linked to a client in the CRM, it is re-filed as Client or Prospect, unless you filed it yourself.',
  },
  {
    title: 'Quick actions',
    body: 'Under the filing actions, a row of quick actions depends on the category. Unsubscribe appears when the sender offers a one-click unsubscribe; after it, the thread is Done and later mail from that sender skips the inbox. Not relevant, on a new contact, marks the thread Done and sends that address’s next mail to Other. Mark all done, on Promotions and Newsletters, asks first and then closes up to 100 open threads at a time, with Continue for the next batch; clearing a pile this way teaches nothing about any one sender. Forward to accounting, on Bills, sends the latest message to your accounting mailbox when Finance is on and that mailbox is connected. A Receivables thread lists the invoices it mentions under Invoices in this thread, and Mark paid there is for workspace admins. A Payments thread linked to a client offers Open billing.',
  },
  {
    title: 'Reply from your own mailbox',
    body: 'Reply sends from your own Gmail mailbox, in the same thread. It goes to the person who wrote the message, or, when you reply to a message you sent, to the people you wrote to. The confirmation names who it went to. The composer has the same Drive button and Add follow-up as a client’s Conversation tab. Drive opens in the thread’s client folder when the thread belongs to a client. Add follow-up makes a task once the reply has sent, linked to the thread’s client when it has one.',
  },
  {
    title: 'Work through several threads',
    body: 'Tick the box on each row to act on several threads at once: shift-click ticks a range, and x ticks the focused row. The bar that appears offers Done (Move to inbox in the Done tab) and Move to a category. Threads that fail stay ticked so you can try again. On a touch screen, swipe a row right: a short swipe shows the Done button, a long one marks it done at once. On a keyboard, j and k move between threads, e marks done, u toggles read, s stars, and Escape clears the ticks. Drag the edge of the thread list or the client column to resize it, or use the arrow keys on the handle; double-click resets it. In dark mode, an email that sets its own colours is shown on a light background, as it was designed.',
  },
  {
    title: 'Inbox settings',
    body: 'Inbox settings, from the gear in the inbox toolbar, has three parts, and each control saves on its own. Categories sets where each category goes: Primary, Other or Skip inbox. Changing one moves its open threads now. Skip inbox archives new mail in Gmail and files it straight to Done; a sender the inbox has learned about follows what it learned. Unsorted is fixed to Primary. Daily digest sends a summary of the day at the hour you choose, in your own timezone, to the bell and as a Slack direct message when Slack is connected; nothing is sent on a quiet day. What Revvly has learned lists the senders the inbox now files on its own, with Reset to start over for one; threads keep the category they were filed in.',
    where: ['Inbox', 'Inbox settings'],
    whereNote: 'Your timezone is set on My Account Settings.',
  },
  {
    title: 'Privacy',
    body: 'Your inbox is yours alone. Nobody else in the workspace sees it, admins included. The text Revvly keeps to show your threads is stored encrypted. Mail that the rules and what you taught cannot sort goes to an AI step that reads its subject and body, but never the sender’s address or name. Personal threads are never used to learn from, and the Assistant sees them only when you ask for the Personal category by name.',
    figure: (
      <Figure
        title="How a new message is filed"
        caption="Your own choice always wins, and mail nothing could decide stays in Primary as Unsorted."
      >
        <Flow
          steps={[
            { title: 'New mail in Gmail', text: 'It reaches your inbox in Revvly.' },
            {
              title: 'Who sent it',
              text: 'A client, a prospect, a colleague or a known service.',
            },
            { title: 'What you taught it', text: 'Your moves, replies and archives.' },
            {
              title: 'AI step',
              text: 'Subject and body only, never the address or name.',
              tone: 'muted',
            },
            {
              title: 'Filed',
              text: 'In a category and a tab, or Unsorted in Primary.',
              tone: 'accent',
            },
          ]}
        />
      </Figure>
    ),
  },
  {
    title: 'The Assistant and Claude',
    body: (
      <>
        The <Link to="/docs/assistant">Revvly Assistant</Link> and Claude over{' '}
        <Link to="/docs/api/mcp">MCP</Link> can read your inbox with three tools: list_inbox,
        get_inbox_summary (what needs your reply) and explain_inbox_category (why a thread has its
        category). A fourth, move_inbox_item, re-files a thread after you approve it on a card. They
        always work on the inbox of the person asking, never a colleague’s, and never return message
        bodies. An MCP connection made with an API key has no person behind it, so the inbox read
        tools refuse it.
      </>
    ),
  },
];

export function WorkInboxGuide() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Sort your mail with the Work Inbox — Revvly Docs';
  }, []);

  return (
    <div className="tn-page">
      <nav className="dp-crumbs" aria-label="Breadcrumb">
        <Link to="/docs/guides">Guides</Link>
        <span aria-hidden="true">›</span>
        <span className="dp-crumb-current">Work Inbox</span>
      </nav>

      <header className="dp-header">
        <span className="rv-eyebrow-mini">Guide</span>
        <h1>
          Sort your mail with the Work <em>Inbox</em>.
        </h1>
        <p className="dp-oneliner">
          Your own Gmail, inside Revvly, sorted into categories with the client file beside each
          thread. Mail from clients and your team comes first, the rest waits in Other, and every
          action reaches Gmail. It is private to you. Linking threads to clients is covered in the{' '}
          <Link to="/docs/guides/crm">CRM guide</Link>, and the bell and digest in{' '}
          <Link to="/docs/guides/notifications">Get notified</Link>.
        </p>
      </header>

      {STEPS.map((step, i) => (
        <div key={i} className="dp-section">
          <h2>
            <span style={{ color: 'var(--rv-fg-dark-5)', fontWeight: 400, marginRight: 12 }}>
              {String(i + 1).padStart(2, '0')}
            </span>
            {step.title}
          </h2>
          <p className="dp-plain">{step.body}</p>
          {step.figure}
          {step.where && <UiPath steps={step.where} note={step.whereNote} />}
        </div>
      ))}

      <section className="dp-section">
        <h2>Related</h2>
        <ul className="dp-connects">
          <li>
            <Link
              to="/docs/guides/notifications"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Get notified ›
            </Link>
          </li>
          <li>
            <Link to="/docs/guides/crm" style={{ color: 'inherit', textDecoration: 'none' }}>
              CRM guide ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/reference/assistant-tools"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              What the Revvly Assistant can do ›
            </Link>
          </li>
          <li>
            <Link
              to="/docs/reference/mcp-tools"
              style={{ color: 'inherit', textDecoration: 'none' }}
            >
              Claude MCP tool catalogue ›
            </Link>
          </li>
        </ul>
      </section>
    </div>
  );
}
